/**
 * 3D Photo Carousel – vanilla JS
 * Auto-rotates clockwise; click an image to pause & open lightbox.
 * Drag to spin manually. Fully self-contained per section instance.
 */
(function () {
  class PhotoCarousel3D {
    constructor(el) {
      this.root = el;
      this.cylinder = el.querySelector('.photo-carousel-3d__cylinder');
      this.cards = Array.from(el.querySelectorAll('.photo-carousel-3d__card'));
      this.lightbox = el.querySelector('.photo-carousel-3d__lightbox');
      this.lightboxImg = this.lightbox.querySelector('img');
      this.closeBtn = this.lightbox.querySelector('.photo-carousel-3d__lightbox-close');

      this.count = this.cards.length;
      if (this.count === 0) return;

      this.speed = parseFloat(el.dataset.speed) || 0.3; // degrees per frame
      this.angle = 0;
      // Respect reduced-motion preference: keep drag/lightbox, skip auto-spin
      this.prefersReducedMotion =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.isSpinning = !this.prefersReducedMotion;
      this.isPaused = false; // lightbox pause
      this.isVisible = true; // IntersectionObserver gate
      this.rafId = null;
      this._lastFocusedCard = null;

      // Drag state
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragStartAngle = 0;
      this.velocity = 0;
      this.lastDragX = 0;
      this.lastDragTime = 0;

      this._positionCards();
      this._bindEvents();
      this._bindResize();
      this._bindVisibility();
      this._spin();
    }

    /* ---- Pause the rAF loop while the carousel is off-screen ---- */
    _bindVisibility() {
      if (!('IntersectionObserver' in window)) return;
      var self = this;
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          self.isVisible = entry.isIntersecting;
          if (self.isVisible && self.rafId === null) {
            self._spin(); // restart loop on re-entry
          }
        });
      });
      observer.observe(this.root);
    }

    /* ---- Position cards around a cylinder ---- */
    _positionCards() {
      var step = 360 / this.count;
      // Compute radius based on card width so cards don't overlap
      var cardWidth = this.cylinder.offsetWidth;
      var radius = Math.round(cardWidth / (2 * Math.tan(Math.PI / this.count)));
      // Responsive minimum radius – wider on larger screens
      var vw = window.innerWidth;
      var minRadius = vw >= 1200 ? 500 : vw >= 750 ? 400 : 300;
      if (radius < minRadius) radius = minRadius;
      this.radius = radius;

      this.cards.forEach(function (card, i) {
        var rot = step * i;
        card.style.transform =
          'rotateY(' + rot + 'deg) translateZ(' + radius + 'px)';
        card.dataset.rotY = rot;
      });
    }

    /* ---- Continuous clockwise rotation ---- */
    _spin() {
      var self = this;
      function frame() {
        // Off-screen: stop the loop entirely; _bindVisibility restarts it
        if (!self.isVisible) {
          self.rafId = null;
          return;
        }
        if (self.isSpinning && !self.isPaused && !self.isDragging) {
          self.angle += self.speed;
        }
        // Apply inertia if dragging ended with velocity
        if (!self.isDragging && Math.abs(self.velocity) > 0.01) {
          self.angle += self.velocity;
          self.velocity *= 0.95; // friction
        } else if (!self.isDragging) {
          self.velocity = 0;
        }
        self.cylinder.style.transform = 'rotateY(' + self.angle + 'deg)';
        self.rafId = requestAnimationFrame(frame);
      }
      self.rafId = requestAnimationFrame(frame);
    }

    /* ---- Recalculate on resize ---- */
    _bindResize() {
      var self = this;
      var timer;
      window.addEventListener('resize', function () {
        clearTimeout(timer);
        timer = setTimeout(function () { self._positionCards(); }, 150);
      });
    }

    /* ---- Events ---- */
    _bindEvents() {
      var self = this;
      var viewport = this.root.querySelector('.photo-carousel-3d__viewport');

      // Click or keyboard (Enter/Space) on a card → pause + lightbox
      var openLightbox = function (card) {
        var img = card.querySelector('img');
        if (img) {
          self.isPaused = true;
          self.isSpinning = false;
          self._lastFocusedCard = card;
          self.lightboxImg.src = img.src;
          self.lightboxImg.alt = img.alt || '';
          self.lightbox.classList.add('is-active');
          self.lightbox.setAttribute('aria-hidden', 'false');
          self.closeBtn.focus();
        }
      };
      this.cards.forEach(function (card) {
        card.addEventListener('click', function () {
          if (self._didDrag) return; // ignore drag-end clicks
          openLightbox(card);
        });
        card.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openLightbox(card);
          }
        });
      });

      // Close lightbox → resume, restore focus
      var closeLightbox = function () {
        self.lightbox.classList.remove('is-active');
        self.lightbox.setAttribute('aria-hidden', 'true');
        self.isPaused = false;
        self.isSpinning = !self.prefersReducedMotion;
        if (self._lastFocusedCard) {
          self._lastFocusedCard.focus();
          self._lastFocusedCard = null;
        }
      };
      this.lightbox.addEventListener('click', closeLightbox);
      this.closeBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        closeLightbox();
      });
      document.addEventListener('keydown', function (e) {
        if (!self.lightbox.classList.contains('is-active')) return;
        if (e.key === 'Escape') {
          closeLightbox();
        } else if (e.key === 'Tab') {
          // Close button is the only focusable element — trap focus on it
          e.preventDefault();
          self.closeBtn.focus();
        }
      });

      // Drag / touch to rotate (with direction detection to allow vertical page scroll)
      var onMouseDown = function (e) {
        self.isDragging = true;
        self._didDrag = false;
        self.velocity = 0;
        self.dragStartX = e.clientX;
        self.dragStartAngle = self.angle;
        self.lastDragX = self.dragStartX;
        self.lastDragTime = Date.now();
      };

      var onTouchStart = function (e) {
        self._didDrag = false;
        self.velocity = 0;
        self.dragStartX = e.touches[0].clientX;
        self._touchStartY = e.touches[0].clientY;
        self.dragStartAngle = self.angle;
        self.lastDragX = self.dragStartX;
        self.lastDragTime = Date.now();
        self._touchDirectionResolved = false;
      };

      var onPointerMove = function (e) {
        var clientX = e.clientX || (e.touches && e.touches[0].clientX) || 0;

        // For touch: detect direction before starting drag
        if (e.touches && !self._touchDirectionResolved) {
          var dx = Math.abs(clientX - self.dragStartX);
          var dy = Math.abs(e.touches[0].clientY - self._touchStartY);
          if (dx < 5 && dy < 5) return;
          self._touchDirectionResolved = true;
          if (dy > dx) return; // Vertical swipe – let page scroll
          self.isDragging = true;
        }

        if (!self.isDragging) return;
        var delta = clientX - self.dragStartX;
        if (Math.abs(delta) > 3) self._didDrag = true;
        self.angle = self.dragStartAngle + delta * 0.3;

        // Track velocity
        var now = Date.now();
        var dt = now - self.lastDragTime;
        if (dt > 0) {
          self.velocity = ((clientX - self.lastDragX) * 0.3) / (dt / 16);
        }
        self.lastDragX = clientX;
        self.lastDragTime = now;
      };

      var onPointerUp = function () {
        self.isDragging = false;
        self._touchDirectionResolved = false;
      };

      viewport.addEventListener('mousedown', onMouseDown);
      viewport.addEventListener('touchstart', onTouchStart, { passive: true });
      window.addEventListener('mousemove', onPointerMove);
      window.addEventListener('touchmove', onPointerMove, { passive: true });
      window.addEventListener('mouseup', onPointerUp);
      window.addEventListener('touchend', onPointerUp);
    }
  }

  /* ---- Init all instances on page ---- */
  function init() {
    document.querySelectorAll('.photo-carousel-3d').forEach(function (el) {
      if (!el._carousel3d) {
        el._carousel3d = new PhotoCarousel3D(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when Shopify theme editor adds/removes sections
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }
})();
